import React, { useState, useEffect, useCallback, useMemo } from 'react';
import http from '../api/http';
import { useToasts } from '../hooks/useToasts';

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [activeRoleId, setActiveRoleId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [cloneSourceRoleId, setCloneSourceRoleId] = useState(null);
  const { showSuccess, showError } = useToasts();

  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await http.get('/management/roles');
      setRoles(data);
      if (!activeRoleId && data.length) setActiveRoleId(data[0].id);
    } catch (error) {
      showError('获取角色列表失败');
    }
  }, [showError, activeRoleId]);

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await http.get('/management/permissions');
      setPermissions(data);
    } catch (error) {
      showError('获取权限列表失败');
    }
  }, [showError]);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, [fetchRoles, fetchPermissions]);

  const activeRole = useMemo(() => roles.find(r => r.id === activeRoleId), [roles, activeRoleId]);

  const togglePermission = (permName) => {
    if (!activeRole || activeRole.name === 'Admin') return; // 不允许修改 Admin
    const has = activeRole.permissions.some(p => p.name === permName);
    const newPerms = has ? activeRole.permissions.filter(p => p.name !== permName) : [...activeRole.permissions, permissions.find(p => p.name === permName)].filter(Boolean);
    setRoles(rs => rs.map(r => r.id === activeRole.id ? { ...r, permissions: newPerms } : r));
  };

  const handleSave = async () => {
    if (!activeRole || activeRole.name === 'Admin') return;
    setSaving(true);
    try {
      const ids = activeRole.permissions.map(p => p.id);
      await http.put(`/management/roles/${activeRole.id}`, { permissions: ids });
      showSuccess(`角色 [${activeRole.name}] 权限已更新`);
      fetchRoles();
    } catch (e) {
      showError('保存失败');
    } finally { setSaving(false); }
  };

  const allPermissionMap = useMemo(() => {
    const map = {}; permissions.forEach(p => { map[p.name] = p; }); return map;
  }, [permissions]);

  // 动态分组: {category: [permissionObj,...]}
  const groupedPermissions = useMemo(() => {
    const groups = {};
    for (const p of permissions) {
      // 工作笔记权限细化分组
      let cat = p.category || '未分组';
      if (String(p.name || '').startsWith('contributions_')) {
        cat = '保险管理';
        if (p.name === 'contributions_view') p.description = '查看保险管理模块';
        if (p.name === 'contributions_manage') p.description = '管理保险管理模块';
      }
      if (p.name === 'manage_work_notes' || p.name === 'manage_own_work_notes') {
        cat = '工作笔记权限';
        if (p.name === 'manage_work_notes') p.description = '管理所有人的工作笔记（管理员专用）';
        if (p.name === 'manage_own_work_notes') p.description = '仅能增删改查自己创建的工作笔记';
      }
      // 专项监测全量查看权限描述强化
      if (p.name === 'special_monitoring_view_all') {
        p.description = '查看全部专项监测任务与记录（含其他负责人）';
      }
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    }
    // 搜索与“仅已选”过滤
    const keyword = search.trim().toLowerCase();
    const rolePermNames = new Set(activeRole?.permissions.map(p=>p.name));
    Object.keys(groups).forEach(cat => {
      let list = groups[cat];
      if (keyword) {
        list = list.filter(p => p.name.toLowerCase().includes(keyword) || (p.description||'').toLowerCase().includes(keyword));
      }
      if (showSelectedOnly && activeRole) {
        list = list.filter(p => rolePermNames.has(p.name));
      }
      groups[cat] = list.sort((a,b) => a.name.localeCompare(b.name));
    });
    return groups;
  }, [permissions, search, showSelectedOnly, activeRole]);

  const handleClone = () => {
    if (!activeRole || activeRole.name==='Admin') return;
    if (!cloneSourceRoleId) { showError('请选择要克隆的来源角色'); return; }
    const source = roles.find(r => r.id === cloneSourceRoleId);
    if (!source) { showError('来源角色不存在'); return; }
    if (source.id === activeRole.id) { showError('不能克隆自身'); return; }
    // Admin 来源可以克隆，但目标 Admin 不允许编辑
    if (activeRole.name==='Admin') return;
    setRoles(rs => rs.map(r => r.id===activeRole.id ? { ...r, permissions: source.permissions.map(p=>({...p})) } : r));
    showSuccess(`已克隆 ${source.name} 的权限到 ${activeRole.name}`);
  };

  return (
    <div style={{ display:'flex', gap:24 }}>
      {/* 左侧角色列表 */}
      <div style={{ width:250, borderRight:'1px solid #e2e8f0', paddingRight:16 }}>
        <h3 style={{ marginTop:0, fontSize:16, letterSpacing:'0.5px' }}>角色列表</h3>
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {roles.map(r => {
            const active = activeRoleId===r.id;
            return (
              <button key={r.id}
                onClick={() => setActiveRoleId(r.id)}
                style={{
                  textAlign:'left',
                  padding:'10px 14px',
                  borderRadius:10,
                  border: active? '1px solid #2563eb':'1px solid #e2e8f0',
                  background: active? 'linear-gradient(135deg,#eff6ff,#dbeafe)':'#fff',
                  cursor:'pointer',
                  boxShadow: active? '0 0 0 2px rgba(37,99,235,0.15)': '0 1px 2px rgba(0,0,0,0.05)',
                  transition:'all .15s'
                }}
              >
                <div style={{ fontWeight:600, color: active? '#1d4ed8':'#334155' }}>{r.name}</div>
                <div style={{ fontSize:12, color: active? '#1e40af':'#64748b' }}>{r.description}</div>
              </button>
            );
          })}
        </div>
      </div>
      {/* 右侧权限编辑区域 */}
      <div style={{ flex:1 }}>
        <h2 style={{ marginTop:0 }}>角色与权限管理</h2>
        {activeRole ? (
          <>
            <div style={{ marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:18, fontWeight:600 }}>当前角色：{activeRole.name}</span>
              {activeRole.name==='Admin' && <span style={{ fontSize:12, color:'#dc2626' }}>Admin 权限不可编辑</span>}
            </div>
            {/* 工具栏：搜索、仅已选、克隆 */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:16, alignItems:'center' }}>
              <input
                placeholder='搜索权限名称或描述...'
                value={search}
                onChange={e=>setSearch(e.target.value)}
                style={{ flex:'1 1 260px', padding:'8px 10px', border:'1px solid #cbd5e1', borderRadius:8 }}
              />
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, padding:'6px 10px', border:'1px solid #cbd5e1', borderRadius:8, background: showSelectedOnly? '#f1f5f9':'#fff' }}>
                <input type='checkbox' checked={showSelectedOnly} onChange={e=>setShowSelectedOnly(e.target.checked)} />
                仅显示已勾选
              </label>
              {activeRole.name!=='Admin' && (
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <select value={cloneSourceRoleId||''} onChange={e=>setCloneSourceRoleId(e.target.value? Number(e.target.value): null)} style={{ padding:'6px 8px', border:'1px solid #cbd5e1', borderRadius:8 }}>
                    <option value=''>选择克隆来源角色</option>
                    {roles.filter(r=> r.id!==activeRole.id).map(r=> <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                  <button onClick={handleClone} style={{ padding:'6px 12px', borderRadius:8, background:'#475569', color:'#fff', border:'none', cursor:'pointer', fontSize:12 }}>克隆权限</button>
                </div>
              )}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
              {Object.entries(groupedPermissions).map(([groupName, permObjs]) => (
                <div key={groupName} style={{ border:'1px solid #e2e8f0', borderRadius:14, padding:'16px 20px', background:'#fff' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                    <strong style={{ fontSize:14 }}>{groupName} <span style={{ color:'#64748b', fontWeight:400 }}>({permObjs.length})</span></strong>
                    {activeRole.name!=='Admin' && permObjs.length>0 && (
                      <div style={{ display:'flex', gap:8 }}>
                        <button style={{ fontSize:12 }} onClick={() => {
                          setRoles(rs => rs.map(r => r.id===activeRole.id ? { ...r, permissions: [...new Set([ ...r.permissions, ...permObjs.map(o=> allPermissionMap[o.name]).filter(Boolean) ])] } : r));
                        }}>全选</button>
                        <button style={{ fontSize:12 }} onClick={() => {
                          const names = permObjs.map(o=>o.name);
                          setRoles(rs => rs.map(r => r.id===activeRole.id ? { ...r, permissions: r.permissions.filter(p => !names.includes(p.name)) } : r));
                        }}>清除</button>
                      </div>
                    )}
                  </div>
                  {permObjs.length === 0 ? (
                    <div style={{ fontSize:12, color:'#94a3b8' }}>无匹配的权限</div>
                  ) : (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px,1fr))', gap:8 }}>
                    {permObjs.map(po => {
                      const checked = !!activeRole.permissions.find(p => p.name === po.name);
                      return (
                        <label key={po.id} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', border:'1px solid '+(checked?'#2563eb':'#e2e8f0'), borderRadius:10, background: checked?'#eff6ff':'#f8fafc', cursor: activeRole.name==='Admin'?'not-allowed':'pointer', fontSize:12 }}>
                          <input type='checkbox' disabled={activeRole.name==='Admin'} checked={checked} onChange={()=>togglePermission(po.name)} />
                          <span style={{ fontSize:12, lineHeight:1.2 }}>{po.description || po.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ marginTop:24 }}>
              <button disabled={activeRole.name==='Admin' || saving} onClick={handleSave} style={{ padding:'10px 20px', borderRadius:8, background:'#2563eb', color:'#fff', border:'none', cursor:'pointer', opacity: (activeRole.name==='Admin'||saving)?0.6:1 }}>
                {saving? '保存中...' : '保存当前角色权限'}
              </button>
            </div>
          </>
        ) : <div>暂无角色数据</div>}
      </div>
    </div>
  );
};

export default RoleManagement;
